import React from "react";
import TabDropdown from "../TabDropdown";
import { useActiveTabLogic } from "./ActiveTabLogic";

// Import all your dropdown content components
import LearningNavigation from "../../../pages/Learnings/LearningDropDown/ActiveTabComponent";

export const ActiveTab = ({ onTabChange, initialTab = null }) => {
  // Define the available tabs
  const tabs = [
    { id: "dashboard", name: "Dashboard", hasDropdown: false },
    { id: "learning", name: "Learning", hasDropdown: true },
    // { id: "reports", name: "Reports", hasDropdown: false },
    // { id: "announcements", name: "Announcements", hasDropdown: true },
    // { id: "chat", name: "Chat", hasDropdown: true }
  ];

  // Use the custom hook to get all the logic
  const {
    activeTab,
    openDropdown,
    mobileMenuOpen,
    mobileExpandedDropdown,
    handleTabClick,
    toggleMobileMenu,
    handleMobileMenuItemClick,
    handleMobileDropdownOptionClick,
    handleDropdownNavigation,
    getLearningDropdownOptions
  } = useActiveTabLogic({ onTabChange, initialTab, tabs });

  // Find the current active tab label if any
  const activeTabLabel = activeTab ? (tabs.find(tab => tab.id === activeTab)?.name || "Dashboard") : "";

  // Rendering dropdown content based on the active dropdown
  const renderDropdownContent = () => {
    switch (openDropdown) {
      case "learning":
        return (
          <LearningNavigation 
            onTabChange={(index) => handleDropdownNavigation(openDropdown, index)} 
          />
        );
      default:
        return null;
    }
  };

  // Render mobile dropdown options
  const renderMobileDropdownOptions = (tabId) => {
    switch (tabId) {
      case "learning":
        const learningOptions = getLearningDropdownOptions();
        
        return (
          <div className="bg-gray-50 border-t border-gray-100">
            {learningOptions.map((option) => (
              <div 
                key={option.path}
                className={`mobile-dropdown-option px-8 py-2.5 text-gray-600 border-b border-gray-100 ${
                  option.disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:bg-gray-100'
                }`}
                onClick={(e) => handleMobileDropdownOptionClick(e, tabId, option)}
              >
                {option.text}
                {option.disabled && <span className="text-xs ml-1">(soon)</span>}
              </div>
            ))}
          </div>
        );
        
      // Add other dropdown options when available
      // case "announcements":
      // case "chat":
        
      default:
        return null;
    }
  };

  return (
    <div className="relative sticky top-[62px] z-10">
      {/* Desktop view */}
      <div className="hidden md:flex items-center gap-2 lg:gap-6 px-2 lg:px-4 border-b border-gray-200 bg-white shadow-md overflow-x-auto">
        {tabs.map((tab) => (
          <div 
            key={tab.id}
            className={`tab-item py-3 py-4 px-2 cursor-pointer relative ${
              activeTab === tab.id ? "text-blue-600" : "text-gray-700 hover:text-gray-900"
            } whitespace-nowrap`}
            onClick={() => handleTabClick(tab.id, tab.hasDropdown)}
          >
            <div className="flex items-center">
              <span className="font-medium text-sm">{tab.name}</span>
              {tab.hasDropdown && (
                <svg 
                  className={`ml-1 w-3 h-3 lg:w-4 lg:h-4 transition-transform duration-300 ${openDropdown === tab.id ? 'rotate-180' : ''}`} 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              )}
            </div>
            {activeTab === tab.id && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600"></div>
            )}
          </div>
        ))}
      </div>

      {/* Mobile view */}
      <div className="md:hidden flex justify-between items-center px-4 py-3 border-b border-gray-200 bg-white shadow-md">
        <div className="text-blue-600 font-medium">
          {activeTabLabel || "Menu"}
        </div>
        <button 
          className="mobile-menu-button text-gray-700 focus:outline-none"
          onClick={toggleMobileMenu}
        >
          <svg 
            className="w-6 h-6" 
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            {mobileMenuOpen 
              ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
            }
          </svg>
        </button>
        
        {/* Mobile menu dropdown */}
        {mobileMenuOpen && (
          <div className="absolute top-full left-0 right-0 bg-white shadow-md z-50 border-b border-gray-200">
            {tabs.map((tab) => (
              <React.Fragment key={tab.id}>
                <div 
                  className={`mobile-menu-option px-4 py-3 flex justify-between items-center ${
                    activeTab === tab.id ? "bg-gray-100 text-blue-600" : "text-gray-700"
                  } border-b border-gray-100 cursor-pointer`}
                  onClick={(e) => handleMobileMenuItemClick(e, tab.id, tab.hasDropdown)}
                >
                  <span>{tab.name}</span>
                  {tab.hasDropdown && (
                    <svg 
                      className={`w-4 h-4 transition-transform duration-300 ${mobileExpandedDropdown === tab.id ? 'rotate-90' : ''}`}
                      fill="none" 
                      viewBox="0 0 24 24" 
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  )}
                </div>
                {/* Show expanded dropdown options if this tab is expanded */}
                {tab.hasDropdown && mobileExpandedDropdown === tab.id && renderMobileDropdownOptions(tab.id)}
              </React.Fragment>
            ))}
          </div>
        )}
      </div>

      {/* Generic dropdown container for desktop view */}
      {openDropdown && (
        <TabDropdown isOpen={Boolean(openDropdown)} tabId={openDropdown}>
          {renderDropdownContent()}
        </TabDropdown>
      )}
    </div>
  );
};

export default ActiveTab;